import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Clock, X, AlertTriangle, FolderKanban, Search } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ClockOutReceipt from './ClockOutReceipt';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

// Warn this many minutes before the configured shift auto-clock-out threshold
const AUTO_OUT_WARN_MINUTES = 10;
// Poll interval for syncing with server (secs)
const POLL_INTERVAL_MS = 30000;

const formatElapsed = (ms) => {
  if (!ms || ms < 0) return '0h 0m';
  const totalMinutes = Math.floor(ms / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const s = Math.floor((ms % 60000) / 1000);
  if (h >= 1) return `${h}h ${m}m`;
  if (m >= 1) return `${m}m ${s}s`;
  return `${s}s`;
};

/**
 * ClockStatusWidget
 *
 * Persistent clock-in indicator with:
 *  - Green/grey status dot
 *  - Live elapsed timer (ticks every second)
 *  - Clock In / Clock Out action with confirmation modal
 *  - Auto-clock-out warning banner (10 minutes before threshold)
 *
 * Props:
 *  - variant: 'compact' (header pill) | 'full' (bottom-sheet / large)
 *  - onStatusChange?: (status) => void  // optional callback after clock in/out
 */
export default function ClockStatusWidget({ variant = 'compact', onStatusChange }) {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);           // { clockedIn, activeClockInAt, shiftDurationHours, todayHours }
  const [now, setNow] = useState(Date.now());
  const [showConfirm, setShowConfirm] = useState(false);
  const [mealBreakPrompt, setMealBreakPrompt] = useState(null); // { shift_hours } | null
  const [receipt, setReceipt] = useState(null); // { hours, deducted } | null
  const [showAutoOutBanner, setShowAutoOutBanner] = useState(false);
  const [dismissedForEntry, setDismissedForEntry] = useState(null);
  const [busy, setBusy] = useState(false);
  // Project picker — shown after the worker clicks "Clock In" so they can
  // tag the upcoming shift with a project (Feb 2026, QuickBooks parity).
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const pollRef = useRef(null);

  // Cookie sessions (Jun 2026): the JWT normally lives in an httpOnly cookie
  // the browser attaches automatically. Only the native shell still keeps a
  // bearer token in localStorage. Gating on localStorage here made the widget
  // invisible for every web session after the cookie migration.
  const authHeader = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/time/dashboard`, authHeader());
      setStatus(res.data);
      onStatusChange && onStatusChange(res.data);
    } catch (err) {
      // token may have expired; stay silent here — auth flow handles redirect
    }
  }, [authHeader, onStatusChange]);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    const tickId = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(pollRef.current);
      clearInterval(tickId);
    };
  }, [fetchStatus]);

  const clockedIn = !!status?.clockedIn;
  const activeAt = status?.activeClockInAt ? new Date(status.activeClockInAt).getTime() : null;
  const elapsedMs = clockedIn && activeAt ? now - activeAt : 0;
  const shiftMs = ((status?.shiftDurationHours ?? 8) * 60 * 60 * 1000);
  const msUntilAutoOut = clockedIn && activeAt ? (activeAt + shiftMs) - now : null;
  const minutesUntilAutoOut = msUntilAutoOut != null ? Math.floor(msUntilAutoOut / 60000) : null;

  // Banner logic: show when within AUTO_OUT_WARN_MINUTES and not dismissed for this entry
  useEffect(() => {
    if (!clockedIn || !status?.activeEntryId) { setShowAutoOutBanner(false); return; }
    if (minutesUntilAutoOut == null) return;
    const inWindow = minutesUntilAutoOut <= AUTO_OUT_WARN_MINUTES && minutesUntilAutoOut >= 0;
    if (inWindow && dismissedForEntry !== status.activeEntryId) {
      setShowAutoOutBanner(true);
    } else {
      setShowAutoOutBanner(false);
    }
  }, [clockedIn, minutesUntilAutoOut, status?.activeEntryId, dismissedForEntry]);

  // After auto-clockout, reset banner state
  useEffect(() => {
    if (!clockedIn) setDismissedForEntry(null);
  }, [clockedIn]);

  const handleClockIn = async (projectId = null) => {
    setBusy(true);
    try {
      const body = { fit_for_duty: true };
      if (projectId) body.project_id = projectId;
      const { data } = await axios.post(`${API}/time/clockin`, body, authHeader());
      toast({
        title: 'Clocked in',
        description: data?.project_number
          ? `Project ${data.project_number} — ${data.project_name}`
          : 'You are now on the clock.',
        duration: 2500,
      });
      setShowProjectPicker(false);
      await fetchStatus();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast({
        title: 'Clock in failed',
        description: (typeof d === 'string' ? d : d?.message) || 'Unable to clock in',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleClockOut = async (deductBreak = false, confirmNoBreak = false) => {
    setBusy(true);
    try {
      const params = deductBreak ? '?deduct_break=true' : confirmNoBreak ? '?confirm_no_break=true' : '';
      const { data } = await axios.post(`${API}/time/clockout${params}`, {}, authHeader());
      // Alberta ESA: a 5h+ shift without a logged meal break returns a 200
      // with meal_break_warning and does NOT clock out — treating that as
      // success left workers stuck on the clock (Aug 2026 prod bug).
      if (!deductBreak && !confirmNoBreak && data?.meal_break_warning) {
        setShowConfirm(false);
        setMealBreakPrompt({ shift_hours: data.shift_hours, threshold: data.break_threshold_hours });
        return;
      }
      setMealBreakPrompt(null);
      // Full-screen receipt so workers always KNOW the clock-out landed.
      setReceipt({ hours: data?.hours_worked, deducted: data?.meal_break_deducted });
      setShowConfirm(false);
      await fetchStatus();
    } catch (err) {
      toast({
        title: 'Clock out failed',
        description: err.response?.data?.detail || 'Unable to clock out',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  // ---------- Compact (desktop header) ----------
  if (variant === 'compact') {
    return (
      <>
        <div className="flex items-center gap-2" data-testid="clock-status-widget">
          <div
            className="flex items-center gap-2 pl-3 pr-1 py-1 rounded-full"
            style={{
              background: clockedIn ? 'rgba(0,200,150,0.10)' : 'rgba(100,116,139,0.10)',
              border: `1px solid ${clockedIn ? 'rgba(0,200,150,0.3)' : 'rgba(100,116,139,0.25)'}`,
            }}
          >
            <span className="relative flex h-2 w-2">
              {clockedIn && (
                <span
                  className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                  style={{ background: '#00C896' }}
                />
              )}
              <span
                className="relative inline-flex rounded-full h-2 w-2"
                style={{ background: clockedIn ? '#00C896' : '#64748B' }}
              />
            </span>
            <div className="text-xs leading-tight hidden xl:block">
              <div
                className="font-medium"
                style={{ color: clockedIn ? '#00C896' : '#A0AEC0' }}
                data-testid="clock-status-label"
              >
                {clockedIn ? 'Clocked In' : 'Not Clocked In'}
              </div>
              {clockedIn && (
                <div className="text-[11px] text-white font-mono" data-testid="clock-elapsed">
                  {formatElapsed(elapsedMs)}
                </div>
              )}
            </div>
            {clockedIn ? (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={busy}
                className="ml-1 px-3 py-1 rounded-full text-xs font-medium transition-colors"
                style={{ background: '#DC2626', color: 'white' }}
                data-testid="clock-out-btn"
              >
                Clock Out
              </button>
            ) : (
              <button
                onClick={() => setShowProjectPicker(true)}
                disabled={busy}
                className="ml-1 px-3 py-1 rounded-full text-xs font-medium transition-colors"
                style={{ background: '#1E88E5', color: 'white' }}
                data-testid="clock-in-btn"
              >
                Clock In
              </button>
            )}
          </div>
        </div>

        {showAutoOutBanner && (
          <AutoOutBanner
            minutesLeft={minutesUntilAutoOut}
            onStayIn={() => setDismissedForEntry(status.activeEntryId)}
            onClockOut={() => { setShowAutoOutBanner(false); setShowConfirm(true); }}
          />
        )}

        {showConfirm && (
          <ConfirmModal
            elapsedMs={elapsedMs}
            onCancel={() => setShowConfirm(false)}
            onConfirm={() => handleClockOut()}
            busy={busy}
          />
        )}
        {mealBreakPrompt && (
          <MealBreakModal
            shiftHours={mealBreakPrompt.shift_hours}
            thresholdHours={mealBreakPrompt.threshold}
            busy={busy}
            onDeduct={() => handleClockOut(true, false)}
            onNoBreak={() => handleClockOut(false, true)}
            onCancel={() => setMealBreakPrompt(null)}
          />
        )}
        <ClockOutReceipt receipt={receipt} onClose={() => setReceipt(null)} />
        {showProjectPicker && (
          <ProjectPickerModal
            authHeader={authHeader}
            busy={busy}
            onCancel={() => setShowProjectPicker(false)}
            onPick={(projectId) => handleClockIn(projectId)}
          />
        )}
      </>
    );
  }

  // ---------- Full (mobile bottom sheet) ----------
  return (
    <div className="flex flex-col items-center gap-4 p-4 w-full" data-testid="clock-status-full">
      <div className="flex items-center gap-3">
        <span
          className="inline-block h-3 w-3 rounded-full"
          style={{ background: clockedIn ? '#00C896' : '#64748B' }}
        />
        <span className="text-base font-semibold text-white">
          {clockedIn ? 'Clocked In' : 'Not Clocked In'}
        </span>
      </div>
      {clockedIn && (
        <div className="text-3xl font-mono text-white" data-testid="clock-elapsed-full">
          {formatElapsed(elapsedMs)}
        </div>
      )}
      {clockedIn ? (
        <button
          onClick={() => setShowConfirm(true)}
          disabled={busy}
          className="w-full py-3 rounded-xl font-semibold transition-colors"
          style={{ background: '#DC2626', color: 'white' }}
          data-testid="clock-out-btn-full"
        >
          Clock Out
        </button>
      ) : (
        <button
          onClick={() => setShowProjectPicker(true)}
          disabled={busy}
          className="w-full py-3 rounded-xl font-semibold transition-colors"
          style={{ background: '#1E88E5', color: 'white' }}
          data-testid="clock-in-btn-full"
        >
          Clock In
        </button>
      )}
      <p className="text-xs text-center" style={{ color: '#64748B' }}>
        Today: {status.todayHours} • Auto-out after {status.shiftDurationHours}h
      </p>

      {showAutoOutBanner && (
        <AutoOutBanner
          minutesLeft={minutesUntilAutoOut}
          onStayIn={() => setDismissedForEntry(status.activeEntryId)}
          onClockOut={() => { setShowAutoOutBanner(false); setShowConfirm(true); }}
        />
      )}

      {showConfirm && (
        <ConfirmModal
          elapsedMs={elapsedMs}
          onCancel={() => setShowConfirm(false)}
          onConfirm={() => handleClockOut()}
          busy={busy}
        />
      )}
      {mealBreakPrompt && (
        <MealBreakModal
          shiftHours={mealBreakPrompt.shift_hours}
          thresholdHours={mealBreakPrompt.threshold}
          busy={busy}
          onDeduct={() => handleClockOut(true, false)}
          onNoBreak={() => handleClockOut(false, true)}
          onCancel={() => setMealBreakPrompt(null)}
        />
      )}
      <ClockOutReceipt receipt={receipt} onClose={() => setReceipt(null)} />
      {showProjectPicker && (
        <ProjectPickerModal
          authHeader={authHeader}
          busy={busy}
          onCancel={() => setShowProjectPicker(false)}
          onPick={(projectId) => handleClockIn(projectId)}
        />
      )}
    </div>
  );
}

// ---------- Helpers (internal) ----------

// Alberta ESA meal-break decision — shown when the server refuses to
// complete a 5h+ clock-out until the worker confirms their break status.
function MealBreakModal({ shiftHours, thresholdHours, busy, onDeduct, onNoBreak, onCancel }) {
  const thr = thresholdHours || 5;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(11,19,33,0.8)', backdropFilter: 'blur(4px)' }}
      data-testid="meal-break-modal"
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6"
        style={{ background: '#1A2433', border: '1px solid #2D3748' }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,160,0,0.15)' }}>
            <AlertTriangle className="w-5 h-5" style={{ color: '#FFA000' }} />
          </div>
          <h3 className="text-lg font-semibold text-white">Did you take a meal break?</h3>
        </div>
        <p className="text-sm mb-5" style={{ color: '#A0AEC0' }}>
          You worked <span className="text-white font-semibold">{shiftHours ? `${shiftHours} hours` : `${thr}+ hours`}</span> without
          a logged meal break. Your company requires a 30-minute break for shifts of {thr} or more hours.
        </p>
        <div className="space-y-2">
          <button
            onClick={onDeduct}
            disabled={busy}
            className="w-full py-3 rounded-lg font-semibold transition-colors disabled:opacity-60"
            style={{ background: '#1E88E5', color: 'white' }}
            data-testid="meal-break-deduct-btn"
          >
            {busy ? 'Clocking out…' : 'Yes — deduct 30-min unpaid break'}
          </button>
          <button
            onClick={onNoBreak}
            disabled={busy}
            className="w-full py-3 rounded-lg font-semibold transition-colors disabled:opacity-60"
            style={{ background: '#2D3748', color: 'white' }}
            data-testid="meal-break-none-btn"
          >
            No — I didn't take a break
          </button>
          <button
            onClick={onCancel}
            disabled={busy}
            className="w-full py-2 rounded-lg text-sm font-medium"
            style={{ color: '#64748B' }}
            data-testid="meal-break-cancel-btn"
          >
            Cancel — stay clocked in
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ elapsedMs, onCancel, onConfirm, busy }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(11,19,33,0.8)', backdropFilter: 'blur(4px)' }}
      data-testid="clock-out-confirm-modal"
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6"
        style={{ background: '#1A2433', border: '1px solid #2D3748' }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(220,38,38,0.15)' }}>
            <Clock className="w-5 h-5" style={{ color: '#DC2626' }} />
          </div>
          <h3 className="text-lg font-semibold text-white">Clock out?</h3>
        </div>
        <p className="text-sm" style={{ color: '#A0AEC0' }}>
          You've worked <span className="text-white font-semibold">{formatElapsed(elapsedMs)}</span> today.
          Are you sure you want to clock out?
        </p>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex-1 py-2 rounded-lg font-medium transition-colors"
            style={{ background: '#2D3748', color: 'white' }}
            data-testid="clock-out-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 py-2 rounded-lg font-medium transition-colors disabled:opacity-60"
            style={{ background: '#DC2626', color: 'white' }}
            data-testid="clock-out-confirm-btn"
          >
            {busy ? 'Clocking out…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AutoOutBanner({ minutesLeft, onStayIn, onClockOut }) {
  const safeMins = Math.max(0, minutesLeft ?? 0);
  return (
    <div
      className="fixed left-1/2 z-[90] w-[95%] max-w-xl px-4 py-3 rounded-xl shadow-lg flex items-center gap-3"
      style={{
        top: 16,
        transform: 'translateX(-50%)',
        background: '#1A2433',
        border: '1px solid #FFA000',
      }}
      data-testid="auto-out-warning-banner"
    >
      <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: '#FFA000' }} />
      <div className="flex-1 text-sm text-white">
        You will be automatically clocked out in{' '}
        <span className="font-semibold" style={{ color: '#FFA000' }}>
          {safeMins} min{safeMins === 1 ? '' : 's'}
        </span>.
        Stay clocked in or clock out now.
      </div>
      <button
        onClick={onStayIn}
        className="px-3 py-1 rounded-md text-xs font-medium"
        style={{ background: '#2D3748', color: 'white' }}
        data-testid="auto-out-stay-btn"
      >
        Stay In
      </button>
      <button
        onClick={onClockOut}
        className="px-3 py-1 rounded-md text-xs font-medium"
        style={{ background: '#DC2626', color: 'white' }}
        data-testid="auto-out-clockout-btn"
      >
        Clock Out
      </button>
      <button
        onClick={onStayIn}
        aria-label="Dismiss"
        className="p-1 rounded-md hover:bg-white/10"
        style={{ color: '#64748B' }}
        data-testid="auto-out-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ProjectPickerModal — shown when a worker clicks "Clock In" so they can
// tag the upcoming shift with a Project (Feb 2026, QuickBooks parity).
// A project is REQUIRED (Sep 2026) — General Work covers non-project time. Searchable on
// project_number / name; debounced to keep typing snappy.
function ProjectPickerModal({ authHeader, busy, onCancel, onPick }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debouncedRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const url = `${API}/projects/search?q=${encodeURIComponent(q || '')}&limit=30`;
      const { data } = await axios.get(url, authHeader());
      setResults(Array.isArray(data) ? data : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  // Debounced search on every keystroke. Default empty query returns the
  // top 30 active projects so the dropdown is useful even without typing.
  useEffect(() => {
    if (debouncedRef.current) clearTimeout(debouncedRef.current);
    debouncedRef.current = setTimeout(() => {
      // eslint-disable-next-line
      runSearch(query);
    }, 200);
    return () => { if (debouncedRef.current) clearTimeout(debouncedRef.current); };
  }, [query, runSearch]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(11,19,33,0.8)', backdropFilter: 'blur(4px)' }}
      data-testid="clock-in-project-picker"
    >
      <div
        className="w-full max-w-md rounded-2xl p-6"
        style={{ background: '#1A2433', border: '1px solid #2D3748' }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.15)' }}>
            <FolderKanban className="w-5 h-5" style={{ color: '#10B981' }} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white">Pick a project</h3>
            <p className="text-xs" style={{ color: '#A0AEC0' }}>
              Your time will be tagged to this project on QuickBooks payroll.
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            aria-label="Close"
            className="p-1 rounded-md hover:bg-white/10"
            style={{ color: '#64748B' }}
            data-testid="picker-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#64748B' }} />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by project # or name…"
            className="w-full pl-9 pr-3 py-2 rounded-md text-sm"
            style={{ background: '#0F1923', color: 'white', border: '1px solid #2D3748' }}
            data-testid="picker-search-input"
          />
        </div>

        <div
          className="rounded-md overflow-y-auto max-h-64 mb-4"
          style={{ background: '#0F1923', border: '1px solid #2D3748' }}
          data-testid="picker-results"
        >
          {loading && (
            <div className="p-4 text-center text-xs" style={{ color: '#A0AEC0' }}>Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="p-4 text-center text-xs" style={{ color: '#A0AEC0' }}>
              No matching projects.
            </div>
          )}
          {!loading && results.map((p) => (
            <button
              key={p.id}
              onClick={() => onPick(p.id)}
              disabled={busy}
              className="w-full px-3 py-2 text-left hover:bg-white/5 border-b last:border-b-0 transition-colors"
              style={{ borderColor: '#2D3748' }}
              data-testid={`picker-pick-${p.id}`}
            >
              <div className="font-mono text-xs" style={{ color: '#10B981' }}>
                {p.project_number}
              </div>
              <div className="text-sm text-white">{p.name}</div>
              {p.customer_name && (
                <div className="text-[11px]" style={{ color: '#A0AEC0' }}>
                  {p.customer_name}
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex-1 py-2 rounded-lg text-sm font-medium"
            style={{ background: '#2D3748', color: 'white' }}
            data-testid="picker-cancel-btn"
          >
            Cancel
          </button>
          <p className="flex-1 text-[11px] self-center" style={{ color: '#64748B' }} data-testid="picker-required-hint">
            {busy ? 'Clocking in…' : 'A project is required — pick General Work for non-project time.'}
          </p>
        </div>
      </div>
    </div>
  );
}

